import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { driversApi } from '../api/drivers';
import { vehiclesApi } from '../api/vehicles';
import { formatPhone } from '../utils/formatters';
import useAuthStore from '../store/authStore';
import toast from 'react-hot-toast';
import { LazyDriverForm, LazyVehicleForm } from '../components/Forms/LazyForms';
import { LazyDriverTripsModal } from '../components/Modals/LazyModals';

const Drivers = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [expandedDriverId, setExpandedDriverId] = useState(null);
  const [showTripsModal, setShowTripsModal] = useState(false);
  const [selectedDriverForTrips, setSelectedDriverForTrips] = useState(null);
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ['drivers', page, pageSize],
    queryFn: () => driversApi.getAll({ page, pageSize }),
  });

  const deleteMutation = useMutation({
    mutationFn: driversApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success('Conductor eliminado exitosamente');
    },
    onError: (error) => {
      console.log('Drivers delete error:', error);
      // Manejar errores específicos del servidor
      if (error.response?.status === 409) {
        // Error de conflicto - conductor tiene viajes asignados
        const message = error.response?.data?.message || 'No se puede eliminar el conductor porque tiene viajes asignados';
        console.log('Drivers delete error message:', message);
        toast.error(message);
        console.log('Toast error called for 409'); // Debug log
      } else if (error.response?.status) {
        // Otros errores HTTP
        const message = error.response?.data?.message || `Error ${error.response.status}: ${error.response.statusText}`;
        toast.error(message);
      } else {
        // Errores de red u otros
        toast.error(error.message || 'Error al eliminar conductor');
      }
    },
  });

  // Fetch vehicles for expanded driver
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useQuery({
    queryKey: ['vehicles', expandedDriverId],
    queryFn: () => vehiclesApi.getByDriverId(expandedDriverId),
    enabled: !!expandedDriverId,
  });

  const vehicles = Array.isArray(vehiclesData) ? vehiclesData : (vehiclesData?.data || []);

  const deleteVehicleMutation = useMutation({
    mutationFn: vehiclesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Vehículo eliminado exitosamente');
    },
    onError: (error) => {
      if (error.response?.status === 409) {
        const message = error.response?.data?.message || 'No se puede eliminar el vehículo porque tiene viajes asignados';
        toast.error(message);
      } else {
        toast.error(error.message || 'Error al eliminar vehículo');
      }
    },
  });

  console.log('Drivers data:', data);
  console.log('Drivers error:', error);

  // Handle both array and wrapped response formats
  const drivers = Array.isArray(data) ? data : (data?.data || []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        {hasPermission('canManageDrivers') && (
          <button 
            className="btn btn--primary" 
            onClick={() => {
              setEditingDriver(null);
              setShowForm(true);
            }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '8px' }}>add</span>
            Nuevo Conductor
          </button>
        )}
      </div>

      <div style={{ marginTop: 0 }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando...</div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7, color: '#ef4444' }}>
            Error al cargar conductores: {error.message || 'Error desconocido'}
          </div>
        ) : drivers.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>No hay conductores disponibles</div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid rgba(255,255,255,.10)', background: 'var(--panel-bg)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,.07)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Nombre</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Teléfono</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Email</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Licencia</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Estado</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((driver) => (
                  <>
                    <tr key={driver.id} style={{ borderTop: '1px solid rgba(255,255,255,.08)', color: '#e5e5e5' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '600' }}>
                        {driver.nombre} {driver.apellido_paterno} {driver.apellido_materno}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{formatPhone(driver.telefono)}</td>
                      <td style={{ padding: '12px 16px' }}>{driver.email || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>{driver.licencia || '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span className={`badge ${driver.activo === 1 || driver.activo === true || driver.activo === 'true' ? 'green' : 'gray'}`}>
                          {driver.activo === 1 || driver.activo === true || driver.activo === 'true' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {hasPermission('canManageDrivers') ? (
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                            {/* Ver Servicios */}
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <button
                                onClick={() => {
                                  setSelectedDriverForTrips(driver);
                                  setShowTripsModal(true);
                                }}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  background: '#8b5cf6',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  transition: 'all 0.2s',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#7c3aed';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '1';
                                  if (tooltip) tooltip.style.visibility = 'visible';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#8b5cf6';
                                  e.currentTarget.style.transform = 'scale(1)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '0';
                                  if (tooltip) tooltip.style.visibility = 'hidden';
                                }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>local_shipping</span>
                              </button>
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  padding: '6px 12px',
                                  background: '#1a1a1a',
                                  color: '#fff',
                                  fontSize: '12px',
                                  borderRadius: '6px',
                                  whiteSpace: 'nowrap',
                                  pointerEvents: 'none',
                                  opacity: 0,
                                  visibility: 'hidden',
                                  transition: 'opacity 0.2s, visibility 0.2s',
                                  zIndex: 1000,
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                }}
                              >
                                Ver servicios
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderTop: '6px solid #1a1a1a'
                                  }}
                                />
                              </div>
                            </div>

                            {/* Ver Vehículos */}
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <button
                                onClick={() => {
                                  setExpandedDriverId(expandedDriverId === driver.id ? null : driver.id);
                                }}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  background: '#3b82f6',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  transition: 'all 0.2s',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#2563eb';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '1';
                                  if (tooltip) tooltip.style.visibility = 'visible';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#3b82f6';
                                  e.currentTarget.style.transform = 'scale(1)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '0';
                                  if (tooltip) tooltip.style.visibility = 'hidden';
                                }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>directions_car</span>
                              </button>
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  padding: '6px 12px',
                                  background: '#1a1a1a',
                                  color: '#fff',
                                  fontSize: '12px',
                                  borderRadius: '6px',
                                  whiteSpace: 'nowrap',
                                  pointerEvents: 'none',
                                  opacity: 0,
                                  visibility: 'hidden',
                                  transition: 'opacity 0.2s, visibility 0.2s',
                                  zIndex: 1000,
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                }}
                              >
                                Ver vehículos
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderTop: '6px solid #1a1a1a'
                                  }}
                                />
                              </div>
                            </div>

                            {/* Editar */}
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <button
                                onClick={() => {
                                  setEditingDriver(driver);
                                  setShowForm(true);
                                }}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  background: '#3b82f6',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  transition: 'all 0.2s',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#2563eb';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '1';
                                  if (tooltip) tooltip.style.visibility = 'visible';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#3b82f6';
                                  e.currentTarget.style.transform = 'scale(1)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '0';
                                  if (tooltip) tooltip.style.visibility = 'hidden';
                                }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>edit</span>
                              </button>
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  padding: '6px 12px',
                                  background: '#1a1a1a',
                                  color: '#fff',
                                  fontSize: '12px',
                                  borderRadius: '6px',
                                  whiteSpace: 'nowrap',
                                  pointerEvents: 'none',
                                  opacity: 0,
                                  visibility: 'hidden',
                                  transition: 'opacity 0.2s, visibility 0.2s',
                                  zIndex: 1000,
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                }}
                              >
                                Editar
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderTop: '6px solid #1a1a1a'
                                  }}
                                />
                              </div>
            </div>

                            {/* Eliminar */}
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <button
                                onClick={() => {
                                  if (confirm('¿Estás seguro de que quieres eliminar este conductor?')) {
                                    deleteMutation.mutate(driver.id);
                                  }
                                }}
                                disabled={deleteMutation.isPending}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  background: '#ef4444',
                                  color: '#fff',
                                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  transition: 'all 0.2s',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                  opacity: deleteMutation.isPending ? 0.6 : 1
                                }}
                                onMouseEnter={(e) => {
                                  if (!deleteMutation.isPending) {
                                    e.currentTarget.style.background = '#dc2626';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                    const tooltip = e.currentTarget.nextElementSibling;
                                    if (tooltip) tooltip.style.opacity = '1';
                                    if (tooltip) tooltip.style.visibility = 'visible';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#ef4444';
                                  e.currentTarget.style.transform = 'scale(1)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '0';
                                  if (tooltip) tooltip.style.visibility = 'hidden';
                                }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>delete</span>
                              </button>
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  padding: '6px 12px',
                                  background: '#1a1a1a',
                                  color: '#fff',
                                  fontSize: '12px',
                                  borderRadius: '6px',
                                  whiteSpace: 'nowrap',
                                  pointerEvents: 'none',
                                  opacity: 0,
                                  visibility: 'hidden',
                                  transition: 'opacity 0.2s, visibility 0.2s',
                                  zIndex: 1000,
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                }}
                              >
                                Eliminar
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderTop: '6px solid #1a1a1a'
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span style={{ opacity: 0.5, fontSize: '14px' }}>Sin permisos</span>
                        )}
                      </td>
                    </tr>
                    {expandedDriverId === driver.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: '20px', background: 'rgba(255,255,255,.03)' }}>
                        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#eaeaea' }}>
                            Vehículos de {driver.nombre} {driver.apellido_paterno} {driver.apellido_materno}
                          </h3>
                          {hasPermission('canManageVehicles') && (
                            <button 
                              className="btn btn--primary" 
                              onClick={() => {
                                setSelectedDriverId(driver.id);
                                setEditingVehicle(null);
                                setShowVehicleForm(true);
                              }}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', padding: '8px 16px' }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>add</span>
                              Nuevo Vehículo
                            </button>
                          )}
                </div>
                        
                        {isLoadingVehicles ? (
                          <div style={{ padding: '20px', textAlign: 'center', opacity: 0.7 }}>Cargando vehículos...</div>
                        ) : vehicles.length === 0 ? (
                          <div style={{ padding: '20px', textAlign: 'center', opacity: 0.7 }}>No hay vehículos asignados</div>
                        ) : (
                          <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,.08)', background: 'var(--panel-alt)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                              <thead>
                                <tr style={{ background: 'rgba(255,255,255,.05)' }}>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>Marca</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>Modelo</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>Año</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>Placas</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>Estado</th>
                                  {hasPermission('canManageVehicles') && (
                                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>Acciones</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {vehicles.map((vehicle) => (
                                  <tr key={vehicle.id} style={{ borderTop: '1px solid rgba(255,255,255,.06)' }}>
                                    <td style={{ padding: '10px 12px', fontSize: '14px' }}>{vehicle.marca}</td>
                                    <td style={{ padding: '10px 12px', fontSize: '14px' }}>{vehicle.modelo}</td>
                                    <td style={{ padding: '10px 12px', fontSize: '14px' }}>{vehicle.ano}</td>
                                    <td style={{ padding: '10px 12px', fontSize: '14px', fontFamily: 'monospace' }}>{vehicle.placas}</td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                      <span className={`badge ${vehicle.activo === 1 || vehicle.activo === true ? 'green' : 'gray'}`} style={{ fontSize: '11px', padding: '4px 8px' }}>
                                        {vehicle.activo === 1 || vehicle.activo === true ? 'Activo' : 'Inactivo'}
                  </span>
                                    </td>
                                    {hasPermission('canManageVehicles') && (
                                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                                          {/* Editar */}
                                          <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button 
                        onClick={() => {
                                                setSelectedDriverId(driver.id);
                                                setEditingVehicle(vehicle);
                                                setShowVehicleForm(true);
                                              }}
                                              style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                background: '#3b82f6',
                                                color: '#fff',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: 0,
                                                transition: 'all 0.2s',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.background = '#2563eb';
                                                e.currentTarget.style.transform = 'scale(1.05)';
                                                const tooltip = e.currentTarget.nextElementSibling;
                                                if (tooltip) tooltip.style.opacity = '1';
                                                if (tooltip) tooltip.style.visibility = 'visible';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#3b82f6';
                                                e.currentTarget.style.transform = 'scale(1)';
                                                const tooltip = e.currentTarget.nextElementSibling;
                                                if (tooltip) tooltip.style.opacity = '0';
                                                if (tooltip) tooltip.style.visibility = 'hidden';
                                              }}
                                            >
                                              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>edit</span>
                      </button>
                                            <div
                                              style={{
                                                position: 'absolute',
                                                bottom: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                marginBottom: '6px',
                                                padding: '6px 12px',
                                                background: '#1a1a1a',
                                                color: '#fff',
                                                fontSize: '12px',
                                                borderRadius: '6px',
                                                whiteSpace: 'nowrap',
                                                pointerEvents: 'none',
                                                opacity: 0,
                                                visibility: 'hidden',
                                                transition: 'opacity 0.2s, visibility 0.2s',
                                                zIndex: 1000,
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                              }}
                                            >
                                              Editar
                                              <div
                                                style={{
                                                  position: 'absolute',
                                                  top: '100%',
                                                  left: '50%',
                                                  transform: 'translateX(-50%)',
                                                  width: 0,
                                                  height: 0,
                                                  borderLeft: '6px solid transparent',
                                                  borderRight: '6px solid transparent',
                                                  borderTop: '6px solid #1a1a1a'
                                                }}
                                              />
                                            </div>
                                          </div>

                                          {/* Eliminar */}
                                          <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button 
                        onClick={() => {
                                                if (confirm('¿Estás seguro de que quieres eliminar este vehículo?')) {
                                                  deleteVehicleMutation.mutate(vehicle.id);
                                                }
                                              }}
                                              disabled={deleteVehicleMutation.isPending}
                                              style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                background: '#ef4444',
                                                color: '#fff',
                                                cursor: deleteVehicleMutation.isPending ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: 0,
                                                transition: 'all 0.2s',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                opacity: deleteVehicleMutation.isPending ? 0.6 : 1
                                              }}
                                              onMouseEnter={(e) => {
                                                if (!deleteVehicleMutation.isPending) {
                                                  e.currentTarget.style.background = '#dc2626';
                                                  e.currentTarget.style.transform = 'scale(1.05)';
                                                  const tooltip = e.currentTarget.nextElementSibling;
                                                  if (tooltip) tooltip.style.opacity = '1';
                                                  if (tooltip) tooltip.style.visibility = 'visible';
                                                }
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#ef4444';
                                                e.currentTarget.style.transform = 'scale(1)';
                                                const tooltip = e.currentTarget.nextElementSibling;
                                                if (tooltip) tooltip.style.opacity = '0';
                                                if (tooltip) tooltip.style.visibility = 'hidden';
                                              }}
                                            >
                                              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
                      </button>
                                            <div
                                              style={{
                                                position: 'absolute',
                                                bottom: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                marginBottom: '6px',
                                                padding: '6px 12px',
                                                background: '#1a1a1a',
                                                color: '#fff',
                                                fontSize: '12px',
                                                borderRadius: '6px',
                                                whiteSpace: 'nowrap',
                                                pointerEvents: 'none',
                                                opacity: 0,
                                                visibility: 'hidden',
                                                transition: 'opacity 0.2s, visibility 0.2s',
                                                zIndex: 1000,
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                              }}
                                            >
                                              Eliminar
                                              <div
                                                style={{
                                                  position: 'absolute',
                                                  top: '100%',
                                                  left: '50%',
                                                  transform: 'translateX(-50%)',
                                                  width: 0,
                                                  height: 0,
                                                  borderLeft: '6px solid transparent',
                                                  borderRight: '6px solid transparent',
                                                  borderTop: '6px solid #1a1a1a'
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                </div>
                        )}
                      </td>
                    </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
              </div>
        )}
      </div>

      {showForm && (
        <LazyDriverForm
          driver={editingDriver}
          onClose={() => {
            setShowForm(false);
            setEditingDriver(null);
          }}
          onSuccess={() => {
            setShowForm(false);
            setEditingDriver(null);
          }}
        />
      )}

      {showVehicleForm && (
        <LazyVehicleForm
          vehicle={editingVehicle}
          driverId={selectedDriverId}
          onClose={() => {
            setShowVehicleForm(false);
            setEditingVehicle(null);
            setSelectedDriverId(null);
          }}
          onSuccess={() => {
            setShowVehicleForm(false);
            setEditingVehicle(null);
            setSelectedDriverId(null);
            queryClient.invalidateQueries({ queryKey: ['vehicles', expandedDriverId] });
          }}
        />
      )}

      {showTripsModal && selectedDriverForTrips && (
        <LazyDriverTripsModal
          isOpen={showTripsModal}
          onClose={() => {
            setShowTripsModal(false);
            setSelectedDriverForTrips(null);
          }}
          driverId={selectedDriverForTrips.id}
          driver={selectedDriverForTrips}
        />
      )}
    </div>
  );
};

export default Drivers;
